import React, { useState } from "react";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import {
  Button,
  Dialog,
  type DialogProps,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useApi } from "../hooks/useApi";

interface FeedbackButtonProps {
  isAuthenticated: boolean;
  hidden?: boolean;
  style?: React.CSSProperties;
}

export default function FeedbackButton({ isAuthenticated, hidden = false, style }: FeedbackButtonProps) {
  const [showAuthRequiredDialog, setShowAuthRequiredDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [allowContact, setAllowContact] = useState(true);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const { sendFeedback } = useApi();

  const resetFeedbackForm = () => {
    setFeedbackTitle("");
    setFeedbackText("");
    setAllowContact(true);
  };

  const handleFeedbackClick = () => {
    if (!isAuthenticated) {
      setShowAuthRequiredDialog(true);
      return;
    }

    setShowFeedbackDialog(true);
  };

  const closeFeedbackDialog = () => {
    if (sendingFeedback) {
      return;
    }

    setShowFeedbackDialog(false);
    resetFeedbackForm();
  };

  const handleFeedbackDialogClose: NonNullable<DialogProps["onClose"]> = (_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    closeFeedbackDialog();
  };

  const handleSendFeedback = async () => {
    const normalizedTitle = feedbackTitle.trim();
    const normalizedFeedback = feedbackText.trim();

    if (!normalizedTitle || !normalizedFeedback) {
      return;
    }

    setSendingFeedback(true);
    try {
      const result = await sendFeedback({
        title: normalizedTitle,
        feedback: normalizedFeedback,
        allow_contact: allowContact,
      });

      if (!result.ok) {
        return;
      }

      setShowFeedbackDialog(false);
      resetFeedbackForm();
      setShowThankYouDialog(true);
    } finally {
      setSendingFeedback(false);
    }
  };

  if (hidden) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<FeedbackOutlinedIcon fontSize="small" />}
        style={{
          backgroundColor: "#5f6368",
          color: "#fff",
          fontWeight: 600,
          textTransform: "none",
          ...style,
        }}
        onClick={handleFeedbackClick}
      >
        Feedback
      </Button>

      <Dialog open={showAuthRequiredDialog} onClose={() => setShowAuthRequiredDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Feedback</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Must be logged in to send feedback. Signing up is free!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuthRequiredDialog(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showFeedbackDialog} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Feedback</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={feedbackTitle}
            onChange={(event) => setFeedbackTitle(event.target.value.slice(0, 200))}
            inputProps={{ maxLength: 200 }}
            disabled={sendingFeedback}
          />
          <TextField
            margin="dense"
            label="Feedback"
            multiline
            minRows={5}
            fullWidth
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value.slice(0, 5000))}
            inputProps={{ maxLength: 5000 }}
            disabled={sendingFeedback}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={allowContact}
                onChange={(event) => setAllowContact(event.target.checked)}
                disabled={sendingFeedback}
              />
            )}
            label="It is ok to contact me (via email) for more information on this feedback"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFeedbackDialog} disabled={sendingFeedback}>
            Cancel
          </Button>
          <Button
            onClick={handleSendFeedback}
            variant="contained"
            disabled={sendingFeedback || !feedbackTitle.trim() || !feedbackText.trim()}
          >
            {sendingFeedback ? <CircularProgress size={18} color="inherit" /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showThankYouDialog} onClose={() => setShowThankYouDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thank you</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Thank you very much for your feedback!<br/>This is crucial to making t-accounts the best it can be.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowThankYouDialog(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
